export const createScrollIntentController = ({
    target = window,
    threshold = 0,
    shouldIgnoreEvent = () => false,
    onDirectionChange = () => { }
} = {}) => {
    const minDistance = Math.max(0, Number(threshold) || 0)
    let isStarted = false
    let pendingDirection = null
    let travelledDistance = 0
    let activeTouchId = null
    let touchAxis = null
    let startTouchX = null
    let startTouchY = null
    let lastTouchY = null
    let wheelResetTimer = null

    const resetDistance = () => {
        pendingDirection = null
        travelledDistance = 0
    }

    const reset = () => {
        clearTimeout(wheelResetTimer)
        wheelResetTimer = null
        activeTouchId = null
        touchAxis = null
        startTouchX = null
        startTouchY = null
        lastTouchY = null
        resetDistance()
    }

    const consumeDelta = (delta, source, originalEvent) => {
        if (!Number.isFinite(delta) || delta === 0) return

        const direction = delta > 0 ? 'down' : 'up'

        if (direction !== pendingDirection) {
            pendingDirection = direction
            travelledDistance = Math.abs(delta)
        } else {
            travelledDistance += Math.abs(delta)
        }

        if (travelledDistance < minDistance) return

        onDirectionChange({
            delta: direction === 'down' ? travelledDistance : -travelledDistance,
            direction,
            source,
            originalEvent
        })
        travelledDistance = 0
    }

    const normalizeWheelDelta = (event) => {
        if (event.deltaMode === 1) return event.deltaY * 16
        if (event.deltaMode === 2) return event.deltaY * window.innerHeight
        return event.deltaY
    }

    const handleWheel = (event) => {
        if (shouldIgnoreEvent(event)) return

        clearTimeout(wheelResetTimer)
        consumeDelta(normalizeWheelDelta(event), 'wheel', event)
        wheelResetTimer = setTimeout(resetDistance, 120)
    }

    const findTouch = (touches, identifier) => {
        for (let index = 0; index < touches.length; index += 1) {
            if (touches[index].identifier === identifier) return touches[index]
        }

        return null
    }

    const handleTouchStart = (event) => {
        reset()
        if (event.touches.length !== 1 || shouldIgnoreEvent(event)) return

        const touch = event.touches[0]
        activeTouchId = touch.identifier
        startTouchX = touch.clientX
        startTouchY = touch.clientY
        lastTouchY = touch.clientY
    }

    const handleTouchMove = (event) => {
        if (activeTouchId === null) return
        if (event.touches.length !== 1) {
            reset()
            return
        }

        const touch = findTouch(event.touches, activeTouchId)
        if (!touch) {
            reset()
            return
        }

        const currentY = touch.clientY

        if (touchAxis === null) {
            const totalX = touch.clientX - startTouchX
            const totalY = currentY - startTouchY
            if (Math.max(Math.abs(totalX), Math.abs(totalY)) < minDistance) return

            touchAxis = Math.abs(totalY) > Math.abs(totalX) ? 'vertical' : 'horizontal'
            if (touchAxis === 'horizontal') return

            // 首次确认纵向手势时使用完整位移，避免再次等待一轮阈值。
            consumeDelta(-totalY, 'touch', event)
            lastTouchY = currentY
            return
        }

        if (touchAxis !== 'vertical') return

        // 手指向上移动代表页面向下滚动。
        consumeDelta(lastTouchY - currentY, 'touch', event)
        lastTouchY = currentY
    }

    const handleTouchEnd = (event) => {
        if (activeTouchId === null) return
        if (!findTouch(event.changedTouches, activeTouchId)) return

        reset()
    }

    const start = () => {
        if (isStarted) return

        isStarted = true
        reset()
        target.addEventListener('wheel', handleWheel, { passive: true })
        target.addEventListener('touchstart', handleTouchStart, { passive: true })
        target.addEventListener('touchmove', handleTouchMove, { passive: true })
        target.addEventListener('touchend', handleTouchEnd, { passive: true })
        target.addEventListener('touchcancel', handleTouchEnd, { passive: true })
    }

    const stop = () => {
        if (!isStarted) return

        isStarted = false
        target.removeEventListener('wheel', handleWheel)
        target.removeEventListener('touchstart', handleTouchStart)
        target.removeEventListener('touchmove', handleTouchMove)
        target.removeEventListener('touchend', handleTouchEnd)
        target.removeEventListener('touchcancel', handleTouchEnd)
        reset()
    }

    return {
        reset,
        start,
        stop
    }
}
